import type {
  BackgroundOpacity,
  CaptionTrack,
  FontFamily,
  FontSize,
  Level,
  SubtitlePosition,
  TextColor,
  TextEdgeStyle,
} from "@lumi/player-core";
import { Button } from "@lumi/ui-headless";
import { ClosedCaption, Maximize2, Pause, Play, RotateCcw, RotateCw, Settings, SkipBack } from "lucide-react";
import type { ReactElement } from "react";
import React from "react";
import { TimelineSlider } from "./TimelineSlider";
import { VolumeControl } from "./VolumeControl";

/**
 * Number of seconds to skip backward/forward when using skip buttons
 */
const SKIP_DURATION_SECONDS = 10;

export type ControlsBarProps = Readonly<{
  containerRef: React.RefObject<HTMLDivElement | null>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isPlaying: boolean;
  onTogglePlay: () => void;
  time: number;
  duration: number;
  onSeek: (nextSeconds: number) => void;
  levels: ReadonlyArray<Level>;
  selectedLevel: number | "auto";
  onChangeLevel: (val: number | "auto") => void;
  captions: ReadonlyArray<CaptionTrack>;
  activeCaption: number | "off";
  onChangeCaption: (val: number | "off") => void;
  playbackRate: number;
  onChangePlaybackRate: (rate: number) => void;
  subtitlePosition: SubtitlePosition;
  onChangeSubtitlePosition: (position: SubtitlePosition) => void;
  subtitleFontSize: FontSize;
  onChangeSubtitleFontSize: (size: FontSize) => void;
  subtitleBackgroundOpacity: BackgroundOpacity;
  onChangeSubtitleBackgroundOpacity: (opacity: BackgroundOpacity) => void;
  subtitleTextColor: TextColor;
  onChangeSubtitleTextColor: (color: TextColor) => void;
  subtitleFontFamily: FontFamily;
  onChangeSubtitleFontFamily: (family: FontFamily) => void;
  subtitleTextEdge: TextEdgeStyle;
  onChangeSubtitleTextEdge: (edge: TextEdgeStyle) => void;
  /** VTT sprite sheet URL (from asset.thumbnailVttUrl) */
  vttUrl?: string | null;
  /** Active settings panel ('quality', 'captions', or null) */
  activePanel: "quality" | "captions" | null;
  /** Callback when active panel changes */
  onActivePanelChange: (panel: "quality" | "captions" | null) => void;
}>;

type PlaybackControlsProps = Readonly<{
  isPlaying: boolean;
  onTogglePlay: () => void;
  onWatchFromStart: () => void;
  onSkipBack: () => void;
  onSkipForward: () => void;
}>;

function PlaybackControls({
  isPlaying,
  onTogglePlay,
  onWatchFromStart,
  onSkipBack,
  onSkipForward,
}: PlaybackControlsProps) {
  return (
    <div className="flex items-center gap-2">
      <Button onClick={onWatchFromStart} aria-label="Watch from start" variant="ghost-sm">
        <SkipBack size={20} />
      </Button>
      <Button onClick={onSkipBack} aria-label="Go back 10 seconds" variant="ghost-sm">
        <RotateCcw size={20} />
      </Button>
      <Button onClick={onTogglePlay} aria-label={isPlaying ? "Pause" : "Play"} variant="ghost-sm">
        {isPlaying ? <Pause size={24} /> : <Play size={24} />}
      </Button>
      <Button onClick={onSkipForward} aria-label="Go forward 10 seconds" variant="ghost-sm">
        <RotateCw size={20} />
      </Button>
    </div>
  );
}

type QualityCaptionsProps = Readonly<{
  levels: ReadonlyArray<Level>;
  selectedLevel: number | "auto";
  onChangeLevel: (val: number | "auto") => void;
  captions: ReadonlyArray<CaptionTrack>;
  activeCaption: number | "off";
  onChangeCaption: (val: number | "off") => void;
  playbackRate: number;
  onChangePlaybackRate: (rate: number) => void;
  subtitlePosition: SubtitlePosition;
  onChangeSubtitlePosition: (position: SubtitlePosition) => void;
  subtitleFontSize: FontSize;
  onChangeSubtitleFontSize: (size: FontSize) => void;
  subtitleBackgroundOpacity: BackgroundOpacity;
  onChangeSubtitleBackgroundOpacity: (opacity: BackgroundOpacity) => void;
  subtitleTextColor: TextColor;
  onChangeSubtitleTextColor: (color: TextColor) => void;
  subtitleFontFamily: FontFamily;
  onChangeSubtitleFontFamily: (family: FontFamily) => void;
  subtitleTextEdge: TextEdgeStyle;
  onChangeSubtitleTextEdge: (edge: TextEdgeStyle) => void;
  activePanel: "quality" | "captions" | null;
  onActivePanelChange: (panel: "quality" | "captions" | null) => void;
}>;

function QualityCaptions(props: QualityCaptionsProps) {
  const { activePanel, onActivePanelChange } = props;

  return (
    <div className="relative flex items-center gap-2">
      <Button
        variant="ghost-sm"
        aria-label="Quality Settings"
        onClick={() => onActivePanelChange(activePanel === "quality" ? null : "quality")}
      >
        <Settings size={20} />
      </Button>
      <Button
        variant="ghost-sm"
        aria-label="Caption Settings"
        onClick={() => onActivePanelChange(activePanel === "captions" ? null : "captions")}
      >
        <ClosedCaption size={20} />
      </Button>
    </div>
  );
}

export const ControlsBar = React.memo(function ControlsBar(props: ControlsBarProps): ReactElement {
  const {
    containerRef,
    videoRef,
    isPlaying,
    onTogglePlay,
    time,
    duration,
    onSeek,
    levels,
    selectedLevel,
    onChangeLevel,
    captions,
    activeCaption,
    onChangeCaption,
    playbackRate,
    onChangePlaybackRate,
    subtitlePosition,
    onChangeSubtitlePosition,
    subtitleFontSize,
    onChangeSubtitleFontSize,
    subtitleBackgroundOpacity,
    onChangeSubtitleBackgroundOpacity,
    subtitleTextColor,
    onChangeSubtitleTextColor,
    subtitleFontFamily,
    onChangeSubtitleFontFamily,
    subtitleTextEdge,
    onChangeSubtitleTextEdge,
    vttUrl,
    activePanel,
    onActivePanelChange,
  } = props;

  const handleWatchFromStart = () => onSeek(0);
  // Skip backward/forward, clamping to valid range [0, duration]
  const handleSkipBack = () => onSeek(Math.max(0, time - SKIP_DURATION_SECONDS));
  const handleSkipForward = () => onSeek(Math.min(duration, time + SKIP_DURATION_SECONDS));

  const handleFullscreen = () => {
    // Use container for fullscreen to preserve custom subtitle overlay
    if (containerRef.current?.requestFullscreen) {
      void containerRef.current.requestFullscreen();
    }
  };

  return (
    <div className="relative space-y-4 pb-8">
      <TimelineSlider time={time} duration={duration} onSeek={onSeek} vttUrl={vttUrl} />

      <div className="relative flex items-center justify-center">
        {/* Left Controls - Absolutely positioned to not affect centering */}
        <div className="absolute left-8 flex items-center gap-2">
          <QualityCaptions
            levels={levels}
            selectedLevel={selectedLevel}
            onChangeLevel={onChangeLevel}
            captions={captions}
            activeCaption={activeCaption}
            onChangeCaption={onChangeCaption}
            playbackRate={playbackRate}
            onChangePlaybackRate={onChangePlaybackRate}
            subtitlePosition={subtitlePosition}
            onChangeSubtitlePosition={onChangeSubtitlePosition}
            subtitleFontSize={subtitleFontSize}
            onChangeSubtitleFontSize={onChangeSubtitleFontSize}
            subtitleBackgroundOpacity={subtitleBackgroundOpacity}
            onChangeSubtitleBackgroundOpacity={onChangeSubtitleBackgroundOpacity}
            subtitleTextColor={subtitleTextColor}
            onChangeSubtitleTextColor={onChangeSubtitleTextColor}
            subtitleFontFamily={subtitleFontFamily}
            onChangeSubtitleFontFamily={onChangeSubtitleFontFamily}
            subtitleTextEdge={subtitleTextEdge}
            onChangeSubtitleTextEdge={onChangeSubtitleTextEdge}
            activePanel={activePanel}
            onActivePanelChange={onActivePanelChange}
          />
        </div>

        {/* Centered Playback Controls */}
        <PlaybackControls
          isPlaying={isPlaying}
          onTogglePlay={onTogglePlay}
          onWatchFromStart={handleWatchFromStart}
          onSkipBack={handleSkipBack}
          onSkipForward={handleSkipForward}
        />

        {/* Right Controls - Absolutely positioned to not affect centering */}
        <div className="absolute right-8 flex items-center gap-2">
          <VolumeControl videoRef={videoRef} />
          <Button onClick={handleFullscreen} aria-label="Enter fullscreen" variant="ghost-sm">
            <Maximize2 size={20} />
          </Button>
        </div>
      </div>
    </div>
  );
});
